// Кто пришёл — спрашиваем ДО первого кадра, а не после него.
//
// Витрина целиком живёт за сессией, и анонимному человеку здесь нечего
// показать: его путь — форма входа на `auth.`. Пока ответ на этот вопрос
// приходил уже внутри React (`SessionGate`), анонимный заход выглядел
// мерцанием из четырёх состояний: серая канва → баннер cookie в пустоте →
// спиннер → уход на другой домен. Три из них были нарисованы человеку,
// который никогда не увидит витрину.
//
// Поэтому вопрос задаётся здесь, в `main.tsx`, до `createRoot`. Ответ ловится
// в двух видах: «сессии нет» — уходим, ничего не отрисовав; «вот профиль» —
// он же и есть первый ответ `auth.me`, и его кладут в кэш, чтобы витрина не
// спрашивала то же самое второй раз (`query-client.ts`).
//
// Запрос голым `fetch`, а не клиентом tRPC: клиент живёт внутри провайдеров,
// то есть внутри дерева, которое мы как раз и решаем — монтировать ли.

import type { AuthMe } from "@iec-id/shared/contracts/auth";
import { AuthMeOutputSchema } from "@iec-id/shared/contracts/auth";

/**
 * Что известно о сессии на момент загрузки страницы.
 *
 * `unknown` — не «нет сессии», а «спросить не вышло» (сеть, 500, чужой формат
 * ответа). Разница принципиальна: на «нет» мы уводим человека с домена, и
 * делать это по сетевой ошибке нельзя. В `unknown` витрина монтируется как
 * прежде и разбирается сама — деградация громкая, но не разрушительная.
 */
export type BootSession =
	| { state: "session"; me: AuthMe }
	| { state: "anonymous" }
	| { state: "unknown" };

/** Одиночный (не батч) GET процедуры tRPC: `/trpc/auth.me`. */
const ME_URL = `${import.meta.env.VITE_API_URL ?? ""}/trpc/auth.me`;

export async function probeSession(): Promise<BootSession> {
	try {
		const response = await fetch(ME_URL, {
			// Сессия живёт в cookie на `*.iec.exchange`: без этого запрос анонимен
			// всегда и витрина уводила бы на вход даже вошедшего.
			credentials: "include",
			headers: { accept: "application/json" },
		});

		if (response.status === 401 || response.status === 403) return { state: "anonymous" };
		if (!response.ok) return { state: "unknown" };

		const body: unknown = await response.json();
		const data = (body as { result?: { data?: unknown } } | null)?.result?.data;
		const parsed = AuthMeOutputSchema.safeParse(data);
		return parsed.success ? { state: "session", me: parsed.data } : { state: "unknown" };
	} catch {
		return { state: "unknown" };
	}
}
